// A timecard slip: how a round, or a whole run, reports to the llama who
// worked it. WARM_BEIGE paper with dark ink, two punched holes, a dashed
// head line, and a stamp on that line for the verdict. The llamas work for
// the Countant, so the result comes as a card from the clock. The style is
// in style.css (.slip).

import { glassWidth } from './device';
import { el } from './dom';
import { loadFont, pixelText, repaint, widthOf } from './pixelText';
import { say } from './say';

// The ink on the paper, from the game's palette: DEEPEST_PURPLE for words
// and numbers, DARK_RED for a penalty, MEDIUM_DARK_GREEN for a good mark.
export const SLIP_INK = '#0a061e';
const STAMP_INK = { good: '#468232', bad: '#a53030' } as const;
export type StampKind = keyof typeof STAMP_INK;

const PAIR_FONT = 'buzzer';
const PAIR_SCALE_MOST = 5;
const PAIR_SCALE_LEAST = 2;
// The slip's side padding and the gap between the pair's columns, as
// .slip and .slip-pair set them.
const SLIP_PAD_PX = 14;
const PAIR_GAP_PX = 16;
const STAMP_SCALE = 2;

export interface SlipStamp {
  word: string;
  kind: StampKind;
}

export function slip(head: string, parts: HTMLElement[], stamp: SlipStamp | null): HTMLElement {
  const card = el('div', stamp ? 'slip has-stamp' : 'slip');
  const top = el('div', 'slip-head', head);
  card.append(el('span', 'slip-punch slip-punch-l'), el('span', 'slip-punch slip-punch-r'), top);
  card.append(...parts);
  if (stamp) {
    const mark = el('div', 'slip-stamp');
    mark.style.setProperty('--stamp', STAMP_INK[stamp.kind]);
    mark.append(pixelText(stamp.word, 'compass', STAMP_SCALE, STAMP_INK[stamp.kind]));
    card.append(mark);
  }
  return card;
}

// Two big numbers in two equal columns, each under its label, on one left
// edge. The numbers step down a scale together until both fit a column.
export function slipPair(figures: { label: string; value: string }[]): HTMLElement {
  const row = el('div', 'slip-pair');
  const numbers = figures.map((each) =>
    pixelText(each.value, PAIR_FONT, PAIR_SCALE_MOST, SLIP_INK),
  );
  figures.forEach((each, at) => {
    const box = el('div', 'slip-figure');
    box.append(say(each.label, 'label'), numbers[at] as HTMLCanvasElement);
    row.append(box);
  });
  void loadFont(PAIR_FONT).then((font) => {
    const widest = Math.max(1, ...figures.map((each) => widthOf(font, each.value)));
    const column = (glassWidth() - SLIP_PAD_PX * 2 - PAIR_GAP_PX) / figures.length;
    const scale = Math.max(
      PAIR_SCALE_LEAST,
      Math.min(PAIR_SCALE_MOST, Math.floor(column / widest)),
    );
    if (scale === PAIR_SCALE_MOST) return;
    figures.forEach((each, at) => {
      repaint(numbers[at] as HTMLCanvasElement, each.value, PAIR_FONT, scale, SLIP_INK);
    });
  });
  return row;
}

// A line of the slip: the name, a dot leader, the value. A penalty prints in
// red ink.
export function slipLine(name: string, value: string, penalty = false): HTMLElement {
  const row = el('div', 'slip-line');
  const amount = el('span', penalty ? 'slip-value is-penalty' : 'slip-value', value);
  row.append(el('span', 'slip-name', name), el('span', 'slip-dots'), amount);
  return row;
}
